import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  Avatar,
  Badge,
  Paper,
  Stack
} from '@mui/material';
import {
  Business as BusinessIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  TrendingUp as TrendingUpIcon,
  NewReleases as NewReleasesIcon,
  Refresh as RefreshIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';
import {
  DndContext,
  DragOverlay,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  closestCorners,
  useDroppable
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import {
  CSS
} from '@dnd-kit/utilities';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface DomainRequest {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  companyName?: string;
  domain: string;
  message?: string;
  source: string;
  status: string;
  notes: Array<{
    note: string;
    addedAt: string;
    addedBy: {
      firstName: string;
      lastName: string;
    } | null;
  }>;
  assignedTo?: {
    firstName: string;
    lastName: string;
  };
  contactedAt?: string;
  resolvedAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface CompanyGroup {
  id: string; // Usaremos el dominio como ID único
  domain: string;
  companyName: string;
  status: string; // Estado de la empresa
  totalUsers: number;
  newUsersCount: number; // Nuevos usuarios desde la última revisión
  hasNewActivity: boolean; // Actividad en las últimas 24h
  latestActivity: string;
  requests: DomainRequest[];
}

interface KanbanColumn {
  id: string;
  title: string;
  color: string;
  icon: React.ReactNode;
  companies: CompanyGroup[];
}

interface DraggedItem {
  id: string;
  type: 'company';
  data: CompanyGroup;
}

// Componente para las tarjetas de empresa del Kanban
const CompanyKanbanCard: React.FC<{ 
  company: CompanyGroup; 
  onViewCompany: (company: CompanyGroup) => void;
}> = ({ company, onViewCompany }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: company.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const totalAttempts = company.requests.reduce((sum, req) => sum + req.notes.length, 0);

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      sx={{
        mb: 1,
        cursor: 'default',
        position: 'relative',
        '&:hover': {
          boxShadow: 3,
        }
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box 
          {...listeners} 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            mb: 1,
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' }
          }}
        >
          <DragIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
            <BusinessIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight="bold" noWrap>
              {company.companyName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {company.domain}
            </Typography>
          </Box>
          {company.hasNewActivity && (
            <Chip 
              label="NUEVO" 
              size="small" 
              color="error" 
              sx={{ fontSize: '0.6rem', height: 18 }}
            />
          )}
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip 
              label={`${company.totalUsers} usuarios`}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 20 }}
            />
            <Chip 
              label={`${totalAttempts} intentos`}
              size="small"
              variant="outlined"
              color="primary"
              sx={{ fontSize: '0.65rem', height: 20 }}
            />
          </Box>
        </Box>

        {company.newUsersCount > 0 && (
          <Box sx={{ mb: 1 }}>
            <Chip 
              label={`+${company.newUsersCount} nuevos`}
              size="small"
              color="warning"
              sx={{ fontSize: '0.65rem', height: 18 }}
            />
          </Box>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {new Date(company.latestActivity).toLocaleDateString('es-ES')}
          </Typography>
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              console.log('👆 Button clicked for company:', company.companyName);
              onViewCompany(company);
            }}
            sx={{ 
              zIndex: 10,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                bgcolor: 'primary.light',
                color: 'primary.contrastText'
              }
            }}
            title="Ver detalles de la empresa"
          >
            <ViewIcon fontSize="small" />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

// Componente para el área de drop de la columna
const ColumnDropZone: React.FC<{ columnId: string; color: string; isEmpty: boolean }> = ({ columnId, color, isEmpty }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: columnId,
  });

  if (!isEmpty) return null;

  return (
    <Box
      ref={setNodeRef}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'text.secondary',
        fontSize: '0.875rem',
        fontStyle: 'italic',
        border: '2px dashed',
        borderColor: isOver ? `${color}.main` : 'grey.300',
        borderRadius: 1,
        bgcolor: isOver ? `${color}.100` : 'transparent',
        transition: 'all 0.2s ease',
        zIndex: 1,
      }}
    >
      {isOver ? 'Suelta aquí' : 'Arrastra empresas aquí'}
    </Box>
  );
};

// Componente para las columnas del Kanban
const KanbanColumn: React.FC<{ 
  column: KanbanColumn; 
  onViewCompany: (company: CompanyGroup) => void;
}> = ({ column, onViewCompany }) => {
  const { setNodeRef } = useDroppable({
    id: `${column.id}-column`, // ID diferente para evitar conflictos
  });

  return (
    <Paper 
      ref={setNodeRef}
      sx={{ 
        width: 320, 
        minHeight: 600, 
        p: 2, 
        bgcolor: `${column.color}.50`,
        border: '2px dashed transparent',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: `${column.color}.main`,
          opacity: 0.8
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {column.icon}
        <Typography variant="h6" fontWeight="bold" color={`${column.color}.main`}>
          {column.title}
        </Typography>
        <Chip 
          label={column.companies.length} 
          size="small" 
          color={column.color as any}
          sx={{ ml: 'auto' }}
        />
      </Box>
      
      <SortableContext items={column.companies.map(c => c.id)} strategy={verticalListSortingStrategy}>
        <Box sx={{ minHeight: 500, position: 'relative' }}>
          {column.companies.map((company) => (
            <CompanyKanbanCard key={company.id} company={company} onViewCompany={onViewCompany} />
          ))}
          
          {/* Área de drop específica para columnas vacías */}
          <ColumnDropZone 
            columnId={column.id} 
            color={column.color} 
            isEmpty={column.companies.length === 0} 
          />
        </Box>
      </SortableContext>
    </Paper>
  );
};

const AdminDomainRequestsGrouped: React.FC = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<DomainRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<DomainRequest[]>([]);
  const [companies, setCompanies] = useState<CompanyGroup[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<CompanyGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<DomainRequest | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [activeId, setActiveId] = useState<string | null>(null);
  
  // Configuración de sensores para drag & drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  useEffect(() => {
    fetchRequests();
  }, []);

  // Sincronizar scroll entre barras superior e inferior
  useEffect(() => {
    const topScrollContainer = document.querySelector('.kanban-scroll-container:first-of-type');
    const mainScrollContainer = document.querySelector('.kanban-scroll-container:last-of-type');

    if (!topScrollContainer || !mainScrollContainer) return;

    const syncScroll = (source: Element, target: Element) => {
      target.scrollLeft = source.scrollLeft;
    };

    const handleTopScroll = () => syncScroll(topScrollContainer, mainScrollContainer);
    const handleMainScroll = () => syncScroll(mainScrollContainer, topScrollContainer);

    topScrollContainer.addEventListener('scroll', handleTopScroll);
    mainScrollContainer.addEventListener('scroll', handleMainScroll);

    return () => {
      topScrollContainer.removeEventListener('scroll', handleTopScroll);
      mainScrollContainer.removeEventListener('scroll', handleMainScroll);
    };
  }, [filteredCompanies]);

  // Agrupar solicitudes por empresa
  useEffect(() => {
    const groupedCompanies = createCompanyGroups(requests);
    setCompanies(groupedCompanies);
  }, [requests]);

  // Filtrado en tiempo real sin llamadas al servidor
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCompanies(companies);
    } else {
      const filtered = companies.filter(company => 
        company.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.domain.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.requests.some(req =>
          req.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req.email.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      setFilteredCompanies(filtered);
    }
  }, [companies, searchTerm]);

  // Función para crear grupos de empresas
  const createCompanyGroups = (requestsData: DomainRequest[]): CompanyGroup[] => {
    const grouped = requestsData.reduce((acc: { [key: string]: CompanyGroup }, request) => {
      const domain = request.domain;
      
      if (!acc[domain]) {
        // Determinar si es una empresa nueva (solo 1 solicitud)
        const domainRequests = requestsData.filter(r => r.domain === domain);
        const totalUsers = domainRequests.length;
        
        // Calcular nuevos usuarios (últimas 24h)
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const newUsersCount = domainRequests.filter(r => 
          new Date(r.createdAt) > yesterday
        ).length;

        // Actividad reciente
        const hasNewActivity = domainRequests.some(r => 
          new Date(r.updatedAt) > yesterday || 
          r.notes.some(note => new Date(note.addedAt) > yesterday)
        );

        // Fecha de última actividad
        const latestActivity = Math.max(
          ...domainRequests.map(r => Math.max(
            new Date(r.updatedAt).getTime(),
            ...r.notes.map(note => new Date(note.addedAt).getTime())
          ))
        );

        // Determinar estado de la empresa (usar el estado más avanzado)
        const statusPriority = { pending: 1, in_progress: 2, stand_by: 3, won: 4, lost: 5 };
        const companyStatus = domainRequests.reduce((currentStatus, req) => {
          return statusPriority[req.status as keyof typeof statusPriority] > 
                 statusPriority[currentStatus as keyof typeof statusPriority] 
                 ? req.status : currentStatus;
        }, 'pending');

        acc[domain] = {
          id: domain,
          domain,
          companyName: request.companyName || domain.split('.')[0],
          status: companyStatus,
          totalUsers,
          newUsersCount,
          hasNewActivity,
          latestActivity: new Date(latestActivity).toISOString(),
          requests: domainRequests
        };
      }
      
      return acc;
    }, {});

    // Convertir a array y ordenar por última actividad
    return Object.values(grouped).sort((a, b) => 
      new Date(b.latestActivity).getTime() - new Date(a.latestActivity).getTime()
    );
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await api.getDomainRequests();
      
      if (!response || !response.requests) {
        console.error('Respuesta de API inválida:', response);
        setSnackbar({ open: true, message: 'Error al cargar las solicitudes: respuesta inválida', severity: 'error' });
        return;
      }

      const requestsData: DomainRequest[] = Array.isArray(response.requests) ? response.requests : [];
      setRequests(requestsData);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setSnackbar({ open: true, message: 'Error al cargar las solicitudes', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Crear columnas del Kanban para empresas
  const getKanbanColumns = (): KanbanColumn[] => {
    return [
      {
        id: 'pending',
        title: 'Pendientes',
        color: 'warning',
        icon: <AccessTimeIcon />,
        companies: filteredCompanies.filter(c => c.status === 'pending')
      },
      {
        id: 'in_progress',
        title: 'En Proceso',
        color: 'info',
        icon: <AssignmentIcon />,
        companies: filteredCompanies.filter(c => c.status === 'in_progress')
      },
      {
        id: 'stand_by',
        title: 'Stand By',
        color: 'secondary',
        icon: <AccessTimeIcon />,
        companies: filteredCompanies.filter(c => c.status === 'stand_by')
      },
      {
        id: 'won',
        title: 'Ganados',
        color: 'success',
        icon: <CheckCircleIcon />,
        companies: filteredCompanies.filter(c => c.status === 'won')
      },
      {
        id: 'lost',
        title: 'Perdidos',
        color: 'error',
        icon: <CancelIcon />,
        companies: filteredCompanies.filter(c => c.status === 'lost')
      }
    ];
  };

  // Funciones para drag & drop
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    // Lógica para drag over si es necesaria
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    console.log('🎯 Drag End Event:', { activeId: active.id, overId: over?.id });
    
    if (!over) {
      console.log('❌ No drop target found');
      setActiveId(null);
      return;
    }
    
    const companyId = active.id as string;
    const overId = over.id as string;
    
    console.log('🏢 Company ID:', companyId, 'Over ID:', overId);
    console.log('🔍 All droppable areas:', ['pending', 'in_progress', 'stand_by', 'won', 'lost']);
    console.log('🔍 All company IDs:', filteredCompanies.map(c => c.id));
    
    // Verificar si over.id es una columna válida (con o sin sufijo)
    const validColumns = ['pending', 'in_progress', 'stand_by', 'won', 'lost'];
    let newStatus = '';
    
    // Limpiar el overId en caso de que tenga sufijo -column
    const cleanOverId = overId.replace('-column', '');
    
    if (validColumns.includes(overId) || validColumns.includes(cleanOverId)) {
      // Se soltó en una columna
      newStatus = validColumns.includes(overId) ? overId : cleanOverId;
      const kanbanColumns = getKanbanColumns();
      const targetColumn = kanbanColumns.find(col => col.id === newStatus);
      const isEmpty = targetColumn?.companies.length === 0;
      console.log(`📍 Dropped on column: ${newStatus} (${isEmpty ? 'empty' : 'has companies'})`);
    } else {
      // Se soltó en una tarjeta, encontrar la columna de esa tarjeta
      const targetCompany = filteredCompanies.find(c => c.id === overId);
      if (targetCompany) {
        newStatus = targetCompany.status;
        console.log('🎯 Dropped on company card, status:', newStatus);
      } else {
        console.log('❌ Target company not found for overId:', overId);
        console.log('Available companies:', filteredCompanies.map(c => ({id: c.id, name: c.companyName})));
        setActiveId(null);
        return;
      }
    }
    
    // Encontrar la empresa actual
    const company = companies.find(c => c.id === companyId);
    if (!company) {
      console.log('❌ Company not found:', companyId);
      setActiveId(null);
      return;
    }
    
    console.log(`🔄 Transition: ${company.status} → ${newStatus} for company: ${company.companyName}`);
    
    // Si se suelta en el mismo carril, no hacer nada
    if (company.status === newStatus) {
      console.log('⚪ Same status, no change needed');
      setActiveId(null);
      return;
    }
    
    // Actualización optimista: actualizar el estado inmediatamente en el frontend
    const updatedCompanies = companies.map(c => 
      c.id === companyId ? { ...c, status: newStatus } : c
    );
    setCompanies(updatedCompanies);

    // Hacer la llamada al servidor en segundo plano
    try {
      console.log('🚀 Starting API updates for company requests:', company.requests.length);
      
      // Actualizar el estado de TODAS las solicitudes de la empresa
      const updatePromises = company.requests.map((request, index) => {
        console.log(`📤 Updating request ${index + 1}/${company.requests.length}: ${request._id} to ${newStatus}`);
        return api.updateDomainRequestStatus(request._id, newStatus);
      });
      
      const results = await Promise.all(updatePromises);
      console.log('✅ All API updates completed successfully:', results.length);

      // También actualizar el estado local de las requests individuales
      const updatedRequests = requests.map(r => 
        company.requests.some(cr => cr._id === r._id) 
          ? { ...r, status: newStatus } 
          : r
      );
      setRequests(updatedRequests);

      setSnackbar({ 
        open: true, 
        message: `Estado de ${company.companyName} actualizado exitosamente`, 
        severity: 'success' 
      });
    } catch (error: any) {
      console.error('❌ Error updating company status:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      
      // Revertir el cambio optimista si hay error
      setCompanies(companies);
      setSnackbar({ 
        open: true, 
        message: `Error al actualizar el estado de la empresa: ${error.response?.data?.message || error.message}`, 
        severity: 'error' 
      });
    }
    
    setActiveId(null);
  };

  const [selectedCompany, setSelectedCompany] = useState<CompanyGroup | null>(null);

  const handleViewCompany = (company: CompanyGroup) => {
    console.log('🔍 Opening company details for:', company.companyName);
    setSelectedCompany(company);
    setDetailDialogOpen(true);
  };


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'in_progress': return 'info';
      case 'won': return 'success';
      case 'stand_by': return 'secondary';
      case 'lost': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <AccessTimeIcon />;
      case 'in_progress': return <AssignmentIcon />;
      case 'won': return <CheckCircleIcon />;
      case 'stand_by': return <AccessTimeIcon />;
      case 'lost': return <CancelIcon />;
      default: return <AssignmentIcon />;
    }
  };


  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'Pendiente';
      case 'in_progress': return 'En Proceso';
      case 'won': return 'Ganado';
      case 'stand_by': return 'Stand By';
      case 'lost': return 'Perdido';
      default: return status;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <Typography variant="h6">Cargando pipeline comercial...</Typography>
        </Box>
      </Container>
    );
  }

  const columns = getKanbanColumns();

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          🚀 Pipeline Comercial
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Gestión visual de oportunidades • Drag & Drop para cambiar estados
        </Typography>
      </Box>

      {/* Filtros y Búsqueda */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre, email, empresa o dominio..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title="Actualizar datos">
              <IconButton 
                onClick={fetchRequests}
                color="primary"
                sx={{ height: '56px', width: '56px' }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>


      {/* Tablero Kanban */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <Box sx={{ 
          border: '1px solid', 
          borderColor: 'divider', 
          borderRadius: 1,
          '& .kanban-scroll-container': {
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              background: 'grey.100',
              borderRadius: 1,
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'grey.400',
              borderRadius: 1,
              '&:hover': {
                background: 'grey.600',
              },
            },
          }
        }}>
          {/* Scroll superior */}
          <Box 
            className="kanban-scroll-container"
            sx={{ 
              height: 12, 
              mb: 1,
              '& > div': { minWidth: 300, height: 1, bgcolor: 'transparent' }
            }}
          >
            {columns.map((column) => (
              <Box key={`scroll-${column.id}`} />
            ))}
          </Box>
          
          {/* Contenido principal */}
          <Box className="kanban-scroll-container" sx={{ pb: 2 }}>
            {columns.map((column) => (
              <KanbanColumn key={column.id} column={column} onViewCompany={handleViewCompany} />
            ))}
          </Box>
        </Box>
        
        <DragOverlay>
          {activeId ? (
            <CompanyKanbanCard 
              company={filteredCompanies.find(c => c.id === activeId) || companies.find(c => c.id === activeId)!} 
              onViewCompany={handleViewCompany} 
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Modal de Detalles */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {getStatusIcon(selectedCompany?.status || '')}
            <Box>
              <Typography variant="h6">
                Empresa - {selectedCompany?.companyName}
              </Typography>
              <Chip 
                label={getStatusLabel(selectedCompany?.status || '')} 
                color={getStatusColor(selectedCompany?.status || '') as any}
                size="small"
              />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedCompany && (
            <Box>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 3
              }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Información de la Empresa
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Nombre de la Empresa
                    </Typography>
                    <Typography variant="body1">
                      {selectedCompany.companyName}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Dominio
                    </Typography>
                    <Typography variant="body1">
                      {selectedCompany.domain}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Estado
                    </Typography>
                    <Chip
                      icon={getStatusIcon(selectedCompany.status)}
                      label={getStatusLabel(selectedCompany.status)}
                      color={getStatusColor(selectedCompany.status) as any}
                      size="small"
                    />
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Estadísticas
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total de Usuarios
                    </Typography>
                    <Typography variant="body1">
                      {selectedCompany.totalUsers}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Nuevos Usuarios (24h)
                    </Typography>
                    <Typography variant="body1" color={selectedCompany.newUsersCount > 0 ? 'warning.main' : 'text.primary'}>
                      {selectedCompany.newUsersCount}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total de Intentos
                    </Typography>
                    <Typography variant="body1">
                      {selectedCompany.requests.reduce((sum: number, req: any) => sum + req.notes.length, 0)}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Última Actividad
                    </Typography>
                    <Typography variant="body1">
                      {new Date(selectedCompany.latestActivity).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                  </Box>
                  {selectedCompany.hasNewActivity && (
                    <Chip 
                      label="Actividad Reciente" 
                      color="error" 
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
              </Box>

              {/* Lista de Usuarios */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Usuarios ({selectedCompany.requests.length})
                </Typography>
                <Stack spacing={2} sx={{ maxHeight: 400, overflowY: 'auto' }}>
                  {selectedCompany.requests
                    .sort((a: any, b: any) => {
                      const aLatest = Math.max(
                        new Date(a.updatedAt).getTime(),
                        ...a.notes.map((note: any) => new Date(note.addedAt).getTime())
                      );
                      const bLatest = Math.max(
                        new Date(b.updatedAt).getTime(),
                        ...b.notes.map((note: any) => new Date(note.addedAt).getTime())
                      );
                      return bLatest - aLatest;
                    })
                    .map((request: any, index: number) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        borderRadius: 1,
                        bgcolor: 'grey.50'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {request.firstName} {request.lastName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {request.email}
                          </Typography>
                          {request.phone && (
                            <Typography variant="body2" color="text.secondary">
                              📞 {request.phone}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Chip 
                            label={`${request.notes.length} intentos`}
                            size="small"
                            variant="outlined"
                          />
                          <Typography variant="caption" display="block" color="text.secondary">
                            {new Date(request.updatedAt).toLocaleDateString('es-ES')}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {request.message && (
                        <Typography variant="body2" sx={{ 
                          p: 1, 
                          bgcolor: 'white', 
                          borderRadius: 0.5,
                          border: '1px solid',
                          borderColor: 'grey.300',
                          mb: 1
                        }}>
                          💬 {request.message}
                        </Typography>
                      )}
                      
                      {request.notes.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary" gutterBottom>
                            Últimos intentos:
                          </Typography>
                          {request.notes
                            .sort((a: any, b: any) => new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime())
                            .slice(0, 3)
                            .map((note: any, noteIndex: number) => (
                            <Typography key={noteIndex} variant="caption" display="block" color="text.secondary">
                              • {note.note} - {new Date(note.addedAt).toLocaleDateString('es-ES', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminDomainRequestsGrouped;